import React, { useState, useEffect } from 'react';
import { Play, FileText, Settings, Star, TrendingUp } from 'lucide-react';
import { VideoModal } from '../../src/components/shared';

interface DemoContentProps {
  title: string;
  videoTitle: string;
  content: {
    overview: string;
    process: string;
    features: string;
    benefits: string;
  };
  // URL sync props
  activeSubTab?: string;
  onSubTabChange?: (subTab: string) => void;
}

const DemoContent: React.FC<DemoContentProps> = ({
  title,
  videoTitle,
  content,
  activeSubTab: externalActiveSubTab,
  onSubTabChange,
}) => {
  const [internalActiveSubTab, setInternalActiveSubTab] = useState('overview');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const activeSubTab = externalActiveSubTab || internalActiveSubTab;
  const setActiveSubTab = onSubTabChange || setInternalActiveSubTab;

  const subTabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'process', label: 'Process', icon: Settings },
    { id: 'features', label: 'Features', icon: Star },
    { id: 'benefits', label: 'Benefits', icon: TrendingUp },
  ];

  const getActiveContent = () => {
    switch (activeSubTab) {
      case 'overview':
        return content.overview;
      case 'process':
        return content.process;
      case 'features':
        return content.features;
      case 'benefits':
        return content.benefits;
      default:
        return content.overview;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-3">{title}</h3>
      </div>

      {/* Video Block */}
      <div className="relative">
        <button
          className="relative w-full bg-linear-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden shadow-2xl focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 group"
          style={{ aspectRatio: '16/9' }}
          aria-label={`Play video: ${videoTitle}`}
          onClick={() => setIsVideoModalOpen(true)}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white p-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-6 shadow-lg group-hover:bg-blue-500 transition-colors">
                <Play className="w-8 h-8 ml-1" />
              </div>
              <h4 className="text-xl lg:text-2xl font-semibold mb-3">{videoTitle}</h4>
              <p className="text-slate-300 text-base lg:text-lg">Watch the complete walkthrough</p>
            </div>
          </div>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-linear-to-t from-black/30 to-transparent"></div>
          {/* Play button hover effect */}
          <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/10 transition-colors duration-300"></div>
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="bg-slate-50 rounded-xl border border-slate-200">
        <nav
          role="tablist"
          aria-label={`${title} details`}
          className="flex flex-wrap justify-center lg:justify-start p-2 gap-2"
          onKeyDown={(e) => {
            const currentIndex = subTabs.findIndex((tab) => tab.id === activeSubTab);
            let newIndex = currentIndex;

            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              newIndex = currentIndex > 0 ? currentIndex - 1 : subTabs.length - 1;
              e.preventDefault();
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              newIndex = currentIndex < subTabs.length - 1 ? currentIndex + 1 : 0;
              e.preventDefault();
            }

            if (newIndex !== currentIndex) {
              setActiveSubTab(subTabs[newIndex].id);
              // Focus the new tab
              const tabButton = e.currentTarget.querySelector(
                `[data-subtab="${subTabs[newIndex].id}"]`,
              ) as HTMLButtonElement;
              tabButton?.focus();
            }
          }}
        >
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeSubTab === tab.id}
                aria-controls={`${tab.id}-content`}
                data-subtab={tab.id}
                tabIndex={activeSubTab === tab.id ? 0 : -1}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center space-x-2 py-3 px-4 lg:px-6 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  activeSubTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-slate-600 hover:text-blue-600 hover:bg-white/80'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content Area */}
      <div
        className="bg-slate-50 rounded-xl p-6 lg:p-8 border border-slate-200"
        role="tabpanel"
        id={`${activeSubTab}-content`}
        aria-labelledby={`${activeSubTab}-tab`}
      >
        <div className="prose prose-slate max-w-none">
          {getActiveContent()
            .split('\n')
            .map(
              (paragraph, index) =>
                paragraph.trim() && (
                  <p
                    key={index}
                    className="mb-6 text-slate-700 leading-relaxed text-base lg:text-lg"
                  >
                    {paragraph.trim()}
                  </p>
                ),
            )}
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        title={videoTitle}
      />
    </div>
  );
};

export default DemoContent;
